import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, Workflow as WorkflowIcon, Pencil, Trash2 } from 'lucide-react';
import { workflowsApi, appsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

export function WorkflowsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch workflows from API
  const {
    data: workflowsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => workflowsApi.list(),
  });

  // Fetch apps for the mapping
  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  // Delete workflow mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => workflowsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setDeleteId(null);
    },
  });

  const workflows = workflowsData?.items || [];
  const apps = appsData?.items || [];

  // Create a map of app IDs to app names
  const appMap = new Map(apps.map((app) => [app.id, app.name]));

  const getTriggerLabel = (trigger: string) => {
    try {
      const parsed = JSON.parse(trigger);
      const type = parsed.type || parsed;
      switch (type) {
        case 'manual':
          return 'Manuel';
        case 'event':
          return 'Événement';
        case 'schedule':
          return 'Planifié';
        case 'webhook':
          return 'Webhook';
        default:
          return type;
      }
    } catch {
      return trigger;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Workflows</h2>
          <p className="text-sm text-secondary-500">Automatisez vos processus métier</p>
        </div>
        <Button onClick={() => navigate('/workflows/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nouveau Workflow
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-secondary-500">Chargement des workflows...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement: {(error as Error).message}</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && workflows.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-secondary-300 py-12 dark:border-secondary-700">
          <WorkflowIcon className="h-12 w-12 text-secondary-400" />
          <h3 className="mt-4 text-lg font-medium text-secondary-900 dark:text-white">
            Aucun workflow
          </h3>
          <p className="mt-2 text-sm text-secondary-500">
            Créez votre premier workflow pour automatiser vos processus.
          </p>
          <Button className="mt-4" onClick={() => navigate('/workflows/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Créer un workflow
          </Button>
        </div>
      )}

      {/* Workflows table */}
      {!isLoading && !error && workflows.length > 0 && (
        <div className="rounded-xl border border-secondary-200 bg-white dark:border-secondary-800 dark:bg-secondary-900">
          <table className="w-full">
            <thead>
              <tr className="border-b border-secondary-200 dark:border-secondary-800">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-secondary-500">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-secondary-500">
                  Application
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-secondary-500">
                  Déclencheur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-secondary-500">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-secondary-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200 dark:divide-secondary-800">
              {workflows.map((workflow) => (
                <tr key={workflow.id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-secondary-900 dark:text-white">
                        {workflow.name}
                      </div>
                      {workflow.description && (
                        <div className="text-sm text-secondary-500 line-clamp-1">
                          {workflow.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500">
                    {appMap.get(workflow.app_id) || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500">
                    {getTriggerLabel(workflow.trigger)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        workflow.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : workflow.status === 'paused'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}
                    >
                      {workflow.status === 'active' ? 'Actif' : workflow.status === 'paused' ? 'En pause' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/workflows/${workflow.id}`)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Dialog open={deleteId === workflow.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteId(workflow.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Supprimer le workflow</DialogTitle>
                            <DialogDescription>
                              Êtes-vous sûr de vouloir supprimer "{workflow.name}" ? Cette action est irréversible.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteId(null)}>
                              Annuler
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => deleteMutation.mutate(workflow.id)}
                              disabled={deleteMutation.isPending}
                            >
                              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                              Supprimer
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
